/**
 * Auth slice mutations and proactive access-token refresh scheduling.
 */

import { decodeJwt } from "jose";
import { AUTH_DEFAULT, authSlice, type AuthState } from "../../store/persistence";
import { persistentSliceStore } from "../../store/persistence/runtime/PersistentSliceStore";
import { refreshAccessToken } from "./auth-api.service";

/** Milliseconds before `exp` at which proactive refresh runs. */
const PROACTIVE_REFRESH_LEAD_MS = 60_000;

let proactiveTimerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Reads the current auth slice from the persistence memory store.
 *
 * @returns Current auth state.
 */
export function peekAuthState(): AuthState {
    return persistentSliceStore.peek( authSlice );
}

/**
 * Persists a new access token (keeps PKCE fields untouched).
 *
 * @param accessToken - FlowRide access JWT, or null to clear.
 */
export function setAccessToken( accessToken: string | null ): void {
    const current = peekAuthState();
    persistentSliceStore.setValue( authSlice, {
        ...current,
        accessToken,
    } );
    scheduleProactiveRefresh();
}

/**
 * Stores PKCE verifier and OAuth state before the authorize redirect.
 *
 * @param codeVerifier - PKCE code verifier.
 * @param oauthState - OAuth state parameter.
 */
export function setPkceTransient( codeVerifier: string, oauthState: string ): void {
    const current = peekAuthState();
    persistentSliceStore.setValue( authSlice, {
        ...current,
        "pkceCodeVerifier": codeVerifier,
        oauthState,
    } );
}

/**
 * Clears PKCE verifier and OAuth state after callback success or failure.
 */
export function clearPkceTransient(): void {
    const current = peekAuthState();
    persistentSliceStore.setValue( authSlice, {
        ...current,
        "pkceCodeVerifier": null,
        "oauthState":       null,
    } );
}

/**
 * Clears the entire auth session (access token + PKCE).
 */
export function clearAuthSession(): void {
    cancelProactiveRefresh();
    persistentSliceStore.setValue( authSlice, AUTH_DEFAULT );
}

/**
 * Returns the access-token expiry instant in epoch milliseconds, or null.
 *
 * @param accessToken - Compact JWT.
 * @returns Expiry epoch ms, or null when undecodable.
 */
export function getAccessTokenExpiryMs( accessToken: string ): number | null {
    try {
        const payload = decodeJwt( accessToken );
        if ( typeof payload.exp !== "number" ) {
            return null;
        }

        return payload.exp * 1000;
    } catch {
        return null;
    }
}

/**
 * Reads the `firstname` claim from an access JWT payload (unverified decode).
 *
 * @param accessToken - Compact JWT.
 * @returns Trimmed firstname, or null when missing/undecodable.
 */
export function getAccessTokenFirstname( accessToken: string ): string | null {
    try {
        const payload = decodeJwt( accessToken );
        if ( typeof payload.firstname !== "string" ) {
            return null;
        }
        const firstname = payload.firstname.trim();
        if ( firstname === "" ) {
            return null;
        }

        return firstname;
    } catch {
        return null;
    }
}

/**
 * Reads the `lastname` claim from an access JWT payload (unverified decode).
 *
 * @param accessToken - Compact JWT.
 * @returns Trimmed lastname, or null when missing/undecodable.
 */
export function getAccessTokenLastname( accessToken: string ): string | null {
    try {
        const payload = decodeJwt( accessToken );
        if ( typeof payload.lastname !== "string" ) {
            return null;
        }
        const lastname = payload.lastname.trim();
        if ( lastname === "" ) {
            return null;
        }

        return lastname;
    } catch {
        return null;
    }
}

/**
 * Reads the `birthdate` claim from an access JWT payload (unverified decode).
 *
 * @param accessToken - Compact JWT.
 * @returns `YYYY-MM-DD` birthdate, or null when missing/undecodable.
 */
export function getAccessTokenBirthdate( accessToken: string ): string | null {
    try {
        const payload = decodeJwt( accessToken );
        if ( typeof payload.birthdate !== "string" ) {
            return null;
        }
        const birthdate = payload.birthdate.trim();
        if ( birthdate === "" ) {
            return null;
        }

        return birthdate;
    } catch {
        return null;
    }
}

/**
 * Cancels any pending proactive refresh timer.
 */
export function cancelProactiveRefresh(): void {
    if ( proactiveTimerId != null ) {
        clearTimeout( proactiveTimerId );
        proactiveTimerId = null;
    }
}

/**
 * Schedules a silent refresh shortly before the current access token expires.
 */
export function scheduleProactiveRefresh(): void {
    cancelProactiveRefresh();
    const { accessToken } = peekAuthState();
    if ( accessToken == null || accessToken === "" ) {
        return;
    }
    const expiryMs = getAccessTokenExpiryMs( accessToken );
    if ( expiryMs == null ) {
        return;
    }
    const delayMs = Math.max( 0, expiryMs - Date.now() - PROACTIVE_REFRESH_LEAD_MS );
    proactiveTimerId = setTimeout( () => {
        void ( async() => {
            try {
                const response = await refreshAccessToken();
                setAccessToken( response.access_token );
            } catch {
                // Reactive interceptor will handle the next 401; keep token until then.
            }
        } )();
    }, delayMs );
}

/**
 * Bootstraps session refresh after persistence hydration.
 * Refreshes immediately when the access token is missing or near expiry.
 *
 * @returns Resolves when the bootstrap attempt completes.
 */
export async function bootstrapAuthSession(): Promise<void> {
    const { accessToken } = peekAuthState();
    if ( accessToken == null || accessToken === "" ) {
        return;
    }
    const expiryMs = getAccessTokenExpiryMs( accessToken );
    const needsImmediateRefresh = expiryMs == null || expiryMs - Date.now() <= PROACTIVE_REFRESH_LEAD_MS;
    if ( needsImmediateRefresh ) {
        try {
            const response = await refreshAccessToken();
            setAccessToken( response.access_token );
        } catch {
            clearAuthSession();
        }

        return;
    }
    scheduleProactiveRefresh();
}
